/* Bringing a fresh export into a tracker that already has rows.
 *
 * The pattern this exists for: the client's plan lists every point that has to
 * be built, and a separate export lists the ones that are done, with the
 * details that only exist once somebody has been out there. The plan is the
 * denominator and it does not change. The export arrives every week and fills
 * the plan in.
 *
 * Replacing every row would be wrong twice over. It would throw away the
 * hundred planned points the export has never heard of, and it would wipe
 * anything typed into the app since the last file.
 *
 * So rows are matched on a key. A match updates, a miss adds, and anything the
 * export does not mention is left exactly as it was.
 */

/* Two ids are the same id whatever the case and whatever whitespace the export
   put around them. Everything else is compared as written, because a match on
   an id is the only thing being decided here. */
export const matchKey = (v) =>
  v === null || v === undefined ? "" : String(v).trim().toLowerCase().replace(/\s+/g, " ");

/* Which columns could be matched on: named on both sides, with values.
 *
 * Sorted so the most likely key comes first. A column whose values are unique
 * across every row is an identifier; one with six repeats is a category, and
 * matching on it would fold six rows into one. */
export function matchableColumns(existingColumns = [], incomingColumns = [], incomingRows = []) {
  const distinct = (key) => {
    const seen = new Set();
    for (const r of incomingRows) {
      const k = matchKey(r[key]);
      if (k) seen.add(k);
    }
    return seen.size;
  };
  const filled = (key) => incomingRows.filter((r) => matchKey(r[key])).length;

  return incomingColumns
    .map((c) => ({ column: c, unique: distinct(c.key) === filled(c.key) && filled(c.key) > 0 }))
    .filter((x) => x.unique)
    .map((x) => x.column);
}

/* What a merge would do, worked out before anything is written.
 *
 * Only the columns the export actually carries are copied onto a matched row.
 * The live network export has no Status column; if a merge wrote every column
 * it knew about, every point it touched would have its status blanked, which
 * is the opposite of filling the plan in.
 */
export function planMerge({
  existing = [], incoming = [], incomingColumns = [], fromKey, toKey,
}) {
  if (!fromKey || !toKey) {
    return { error: "Choose the column on each side that identifies a row." };
  }

  const byKey = new Map();
  for (const row of existing) {
    const k = matchKey(row[toKey]);
    /* First one wins. A tracker with the same id twice is already wrong, and
       silently updating both would hide it rather than fix it. */
    if (k && !byKey.has(k)) byKey.set(k, row);
  }

  /* The key column is what matched, not something to copy. Writing the
     export's own id column onto a matched row would leave the tracker with two
     columns of the same identifier, one from the plan and one from the export,
     forever disagreeing about which is the real one. A new row instead gets
     that value written into the tracker's own id column, so it arrives looking
     like everything already there. */
  const carried = incomingColumns.map((c) => c.key).filter((k) => k !== fromKey);
  const updates = [];
  const inserts = [];
  let blankKeys = 0;

  for (const row of incoming) {
    const k = matchKey(row[fromKey]);
    if (!k) { blankKeys += 1; continue; }

    const hit = byKey.get(k);
    if (!hit) {
      const { [fromKey]: id, ...rest } = row;
      inserts.push({ ...rest, [toKey]: id });
      continue;
    }

    /* Only what actually changed, so a re-import of an unchanged file writes
       nothing and the updated_at timestamps stay honest. */
    const patch = {};
    for (const key of carried) {
      const next = row[key] ?? null;
      const now = hit[key] ?? null;
      if (next !== now) patch[key] = next;
    }
    if (Object.keys(patch).length) {
      updates.push({ id: hit._id, data: { ...stripped(hit), ...patch }, changed: Object.keys(patch) });
    }
  }

  const touched = new Set([
    ...updates.map((u) => u.id),
    ...incoming.map((r) => byKey.get(matchKey(r[fromKey]))?._id).filter(Boolean),
  ]);

  return {
    updates,
    inserts,
    matched: incoming.length - inserts.length - blankKeys,
    untouched: existing.length - touched.size,
    blankKeys,
  };
}

const stripped = (row) => {
  const { _id, _index, ...data } = row;
  return data;
};

/* The tracker's columns after a merge: what it had, then anything new the
   export brought, in the export's own order. A tracker that gains Latitude and
   Longitude this week keeps every column it had last week. */
export function mergedColumns(existingColumns = [], incomingColumns = [], fromKey = null) {
  const have = new Set(existingColumns.map((c) => c.key));
  return [
    ...existingColumns,
    ...incomingColumns.filter((c) => !have.has(c.key) && c.key !== fromKey),
  ];
}
